import { Children, forwardRef, isValidElement, useRef, useState, type ReactNode, type SelectHTMLAttributes } from 'react';
import { SelectField } from './SelectField';

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> { label?: string; error?: string }

function readOptions(children: ReactNode, parentDisabled = false): { value: string; label: string; disabled?: boolean }[] {
  return Children.toArray(children).flatMap((child) => {
    if (!isValidElement<{ children?: ReactNode; value?: string | number; disabled?: boolean }>(child)) return [];
    if (child.type !== 'option') return readOptions(child.props.children, parentDisabled || child.props.disabled === true);
    const label = Children.toArray(child.props.children).join('');
    return [{ value: String(child.props.value ?? label), label, disabled: parentDisabled || child.props.disabled }];
  });
}

/** Radix UI visually, native change events and form values for existing consumers. */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(({ label, error, id, className, children, value, defaultValue, onChange, disabled, 'aria-label': ariaLabel, required, ...props }, ref) => {
  const options = readOptions(children);
  const [localValue, setLocalValue] = useState(String(defaultValue ?? options[0]?.value ?? ''));
  const nativeRef = useRef<HTMLSelectElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const selected = value === undefined ? localValue : String(value);
  return <>
    <select {...props} required={required} onInvalid={(event) => { event.preventDefault(); triggerRef.current?.focus(); props.onInvalid?.(event); }} ref={(node) => { nativeRef.current = node; if (typeof ref === 'function') ref(node); else if (ref) ref.current = node; }} hidden aria-hidden="true" tabIndex={-1} value={selected} disabled={disabled} onChange={(event) => { setLocalValue(event.target.value); onChange?.(event); }}>{children}</select>
    <SelectField ref={triggerRef} id={id} label={label} aria-label={ariaLabel} required={required} error={error} className={className} disabled={disabled} options={options} value={selected} onChange={(next) => {
      if (!nativeRef.current) return;
      nativeRef.current.value = next;
      nativeRef.current.dispatchEvent(new Event('change', { bubbles: true }));
    }} />
  </>;
});
Select.displayName = 'Select';
